import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import FlexWrapper from '../FlexWrapper';
import Divider from '../../components/Divider';
import SuggestionMessage from '../../components/EmptyMessage/SuggestionMessage';
import "./ActiveWorkflows.scss"

const DataSynchronisationCoverage = ({ data, displayMode }) => {
	const [expandedFunds, setExpandedFunds] = useState([])
	const [coverageComponent, setCoverageComponent] = useState("Missing")

	const progressCurrent = data && data.uploadStatus && data.uploadStatus.filter(fund => fund.uploadStatus === 'IMPORTED')
	const progressTotal = data && data.uploadStatus && data.uploadStatus.length

	function getFilteredFunds(status) {
		return data && data.uploadStatus
			? data.uploadStatus
				.filter(fund => fund.uploadedData && fund.uploadedData.some(uploadData => uploadData.uploadStatus === status))
				.map(fund => ({
					...fund,
					uploadedData: fund.uploadedData.filter(uploadData => uploadData.uploadStatus === status),
				}))
			: [];
	}

	function getFilteredGroups(status) {
		return data.groupStatus && data.groupStatus.filter(group => {
			if (status === "MISSING") {
				return group.completed !== group.total
			}
			if (status === "IMPORTED") {
				return group.completed === group.total
			}
		})
	}

	const filteredFunds = coverageComponent === "Missing" ? getFilteredFunds('MISSING') : getFilteredFunds('IMPORTED');
	const viewableFunds = filteredFunds && filteredFunds.length > 0 ? filteredFunds : [];
	const filteredGroups = coverageComponent === "Missing" ? getFilteredGroups("MISSING") : getFilteredGroups("IMPORTED");
	const viewableGroups = filteredGroups && filteredGroups.length > 0 ? filteredGroups : [];

	function toggleRowExpansion(fundId) {
		let expansionList = [...expandedFunds];
		if (expandedFunds.includes(fundId)) {
			expansionList = expansionList.filter((id) => id !== fundId)
		} else {
			expansionList = [...expansionList, fundId]
		}
		setExpandedFunds(expansionList)
	}

	return !data ? (
		""
	) : (
		<FlexWrapper gap="s" direction="column" justify="start" align="start" style={{ marginBottom: "1rem" }}>
			<FlexWrapper gap="l" style={{ marginBottom: "0.5rem" }}>
				<p style={{ fontWeight: 600 }}>Coverage: {progressCurrent.length}/{progressTotal}</p>
			</FlexWrapper>
			<FlexWrapper gap="m" className={`mb-m`}>
				<p
					className={`coverage-component-selector ${coverageComponent === "Missing" ? "active-coverage" : ""}`}
					onClick={() => setCoverageComponent("Missing")}
				>
					Missing
				</p>
				<p
					className={`coverage-component-selector ${coverageComponent === "Completed" ? "active-coverage" : ""}`}
					onClick={() => setCoverageComponent("Completed")}
				>
					Completed
				</p>
			</FlexWrapper>

			{(viewableFunds.length === 0) ? (
				<SuggestionMessage message="No data to display" />
			) : (
				<FlexWrapper direction="column" align="start" style={{ width: "100%", paddingBottom: '6rem' }}>
					<div className="worflow-data-synchronisation-task">
						{displayMode === "GROUP"
							? coverageComponent === "Missing"
								? <RenderGroups groups={viewableGroups} groupClass={"missing-fund"} />
								: <RenderGroups groups={viewableGroups} groupClass={"succeeded-fund"} />
							: coverageComponent === "Missing"
								? <RenderFunds funds={viewableFunds} fundClass={"missing-fund"} dividerColor={"danger-dark"} expandedFunds={expandedFunds} toggleRowExpansion={toggleRowExpansion} />
								: <RenderFunds funds={viewableFunds} fundClass={"succeeded-fund"} dividerColor={"positive-dark"} expandedFunds={expandedFunds} toggleRowExpansion={toggleRowExpansion} />
						}
					</div>
				</FlexWrapper>
			)}
		</FlexWrapper>
	);
}

/** Display names for data types whose enum name does not read well on its own. */
const DATA_TYPE_LABELS = {
	QFU_INPUTS: "fund update inputs",
};

/**
 * A line under a data type that is not a file to import: fund update inputs are checked from the other data
 * (the derivation run without saving), so the user fixes the data it names rather than looking for a file.
 */
const DATA_TYPE_HINTS = {
	QFU_INPUTS: "Checked from the other data, not imported. A fund missing here lists what to load or fix.",
};

export const dataTypeHint = (type) => DATA_TYPE_HINTS[type];

/** A data type as the Data Synchronisation card shows it, lower case (the card capitalises it). */
export const dataTypeLabel = (type) => DATA_TYPE_LABELS[type] || (type.includes("_")
	? type.split("_").join(" ").toLowerCase()
	: type.toLowerCase());

/** How many warnings a fund's data types carry: sentences that do not hold the workflow, for the reviewer. */
export const warningCount = (fund) => fund.uploadedData.reduce((n, file) => n + (file.warnings ? file.warnings.length : 0), 0);

const RenderFunds = ({ funds, fundClass, dividerColor, expandedFunds, toggleRowExpansion }) => {
	return funds.map((fund, idx) => (
		<React.Fragment key={`${fundClass}-fund-${idx}`}>
			<FlexWrapper key={`${idx}-FlexWrapper`} onClick={() => toggleRowExpansion(fund.fundId)} direction="column" align="start">
				<FlexWrapper className={`${fundClass}`}>
					<span>
						{fund.name} ({fund.uploadedData.length})
						{warningCount(fund) > 0 && (
							<span className="data-sync-warning-count">{warningCount(fund)} {warningCount(fund) === 1 ? "warning" : "warnings"}</span>
						)}
					</span>
					{expandedFunds.includes(fund.fundId) ? <ChevronUp /> : <ChevronDown />}
				</FlexWrapper>
				{expandedFunds.includes(fund.fundId) && (
					<FlexWrapper className={`${fundClass}-data`} direction="column" align="start">
						{fund.uploadedData.map((file, idx) => {
							const formattedString = dataTypeLabel(file.type);
							return (
								<React.Fragment key={`${fundClass}-expanded-${idx}`}>
									<div style={{ textTransform: "capitalize" }}>{formattedString}</div>
									{/* what to load, where the check can say (QFU inputs); nothing for the other types */}
									{file.messages && file.messages.length > 0 && (
										<ul className="data-sync-gap-messages">
											{file.messages.map((message, i) => <li key={`${fundClass}-gap-${idx}-${i}`}>{message}</li>)}
										</ul>
									)}
									{/* warnings do not hold the workflow: for the reviewer to see (a blank bar on the return graph) */}
									{file.warnings && file.warnings.length > 0 && (
										<ul className="data-sync-warning-messages">
											{file.warnings.map((warning, i) => <li key={`${fundClass}-warning-${idx}-${i}`}>{warning}</li>)}
										</ul>
									)}
									{idx + 1 !== fund.uploadedData.length && (
										<Divider colour={dividerColor} opacity="0.2" />
									)}
								</React.Fragment>
							);
						})}
					</FlexWrapper>
				)}
			</FlexWrapper>
		</React.Fragment>
	));
};

const RenderGroups = ({ groups, groupClass }) => {
	return groups.map((group, idx) => (
		<React.Fragment key={`${groupClass}-group-${idx}`}>
			<FlexWrapper key={`${idx}-FlexWrapper`} direction="column" align="start">
				<FlexWrapper className={`${groupClass}`}>
					{group.offerName} {groupClass === "missing-fund" ? `${group.completed}/${group.total}` : `(${group.completed})`}
				</FlexWrapper>
			</FlexWrapper>
		</React.Fragment>
	));
};




export default DataSynchronisationCoverage;

